import { useCallback } from 'react';

import { link } from '@aline/routes';
import { useNavigate } from 'react-router-dom';
import { $chatCallbacks } from 'src/components/ai/chat/chat-state/chat-state';
import { useDocumentParams } from 'src/helpers';
import { useEditor } from 'src/layout-engine';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

import { useSelectedSuggestionUnit } from './useNavigationHooks';
import { getSuggestionUnitRange } from '../suggestion-units';

/**
 * When asking for a refinement of the selected suggestion, we hand off
 * execution to the AI chat: open the chat drawer and pre-populate the input
 * with a v2 selection mention scoped to the suggestion's range, plus a
 * leading "Refine the highlighted change. Goal: " template that drops the
 * cursor where the user can type their refinement intent.
 *
 * The mention carries `focusSuggestionId` so the server can correlate the
 * SELECTED part back to a specific suggestion (used by the redline subagent
 * to reason about — and eventually selectively revert — the prior edit).
 */
export function useHandleRefine() {
  const editor = useEditor();
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();
  const { selectedSuggestionUnit } = useSelectedSuggestionUnit();
  const handleRefine = useCallback(() => {
    if (!selectedSuggestionUnit) return;

    const range = getSuggestionUnitRange(editor, selectedSuggestionUnit);
    if (!range) return;

    navigate(link.DocumentDetail({ documentId }), { replace: true });
    openDrawer();
    setTimeout(() => {
      $chatCallbacks.insertSelectionMentionV3?.(editor, range, {
        focusSuggestionId: selectedSuggestionUnit.groupId,
        replacePrompt: true,
        textAfter: ' with these instructions: ',
        textBefore: 'Refine this selection ',
      });
    }, 0);
  }, [documentId, editor, navigate, openDrawer, selectedSuggestionUnit]);

  return { handleRefine };
}
